/**
 * AdminMarkerReports.jsx — iter899 (Feb 2026)
 *
 * Admin queue for user-flagged marker values. Admin sees pending reports
 * (default), can Accept (with optional corrected value → lab_entries) or
 * Reject with an audit note.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, RefreshCw, Flag } from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";

const STATUS_TABS = [
  { key: "pending",  label: "Чакащи" },
  { key: "accepted", label: "Приети" },
  { key: "rejected", label: "Отхвърлени" },
  { key: "all",      label: "Всички" },
];

export default function AdminMarkerReports() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("pending");
  const [busyId, setBusyId] = useState(null);
  const [drafts, setDrafts] = useState({});   // id → {value, unit, note}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/marker-reports`, { params: { status } });
      setReports(data.reports || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка при зареждане");
    } finally {
      setLoading(false);
    }
  }, [API, status]);

  useEffect(() => { load(); }, [load]);

  const draftFor = (id) => drafts[id] || {};
  const setDraft = (id, patch) => setDrafts(d => ({ ...d, [id]: { ...d[id], ...patch } }));

  const accept = async (r) => {
    setBusyId(r._id);
    try {
      const d = draftFor(r._id);
      const body = {};
      if (d.value != null && d.value !== "") {
        const n = Number(String(d.value).replace(",", "."));
        if (!Number.isNaN(n)) body.corrected_value = n;
      }
      if (d.unit) body.corrected_unit = d.unit;
      if (d.note) body.resolution_note = d.note;
      const { data } = await axios.post(`${API}/admin/marker-reports/${r._id}/accept`, body);
      toast.success(data.applied_to_lab_entries
        ? "Прието — коригирано в lab_entries"
        : "Прието (без промяна на lab_entries)");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка");
    } finally { setBusyId(null); }
  };

  const reject = async (r) => {
    setBusyId(r._id);
    try {
      const d = draftFor(r._id);
      await axios.post(`${API}/admin/marker-reports/${r._id}/reject`, { resolution_note: d.note || undefined });
      toast.success("Отхвърлено");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка");
    } finally { setBusyId(null); }
  };

  const rowCls = isDark
    ? "border-slate-700 bg-slate-900/50"
    : "border-slate-200 bg-white";

  return (
    <div data-testid="admin-marker-reports-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Flag className="w-4 h-4 text-red-500" />
          <h2 className="text-base font-bold">Марки за корекция</h2>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${
            isDark ? "bg-slate-800 hover:bg-slate-700 text-slate-200" : "bg-slate-100 hover:bg-slate-200 text-slate-700"
          }`}
          data-testid="admin-marker-reports-refresh"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Опресни
        </button>
      </div>

      <div className="flex items-center gap-1.5 mb-4 flex-wrap">
        {STATUS_TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setStatus(t.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
              status === t.key
                ? "bg-gradient-to-r from-pink-500 to-violet-600 text-white"
                : isDark ? "bg-slate-800 text-slate-300" : "bg-slate-100 text-slate-700"
            }`}
            data-testid={`admin-marker-reports-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin opacity-40" />
        </div>
      )}

      {!loading && reports.length === 0 && (
        <div className={`p-6 rounded-xl border text-center text-sm ${rowCls}`}>
          Няма доклади със статус „{status}“.
        </div>
      )}

      <div className="space-y-3">
        {reports.map((r) => {
          const isPending = r.status === "pending";
          const isBusy = busyId === r._id;
          const d = draftFor(r._id);
          return (
            <div
              key={r._id}
              className={`p-4 rounded-xl border ${rowCls}`}
              data-testid={`admin-marker-report-row-${r._id}`}
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-mono font-bold">{r.marker_code}</span>
                    {r.marker_label && (
                      <span className="text-xs opacity-70">— {r.marker_label}</span>
                    )}
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                      r.status === "pending"  ? "bg-amber-500/20 text-amber-500"  :
                      r.status === "accepted" ? "bg-emerald-500/20 text-emerald-500" :
                                                "bg-slate-500/20 text-slate-500"
                    }`}>{r.status}</span>
                  </div>
                  <div className="text-[11px] opacity-60 mt-1">
                    {r.user_email || r.user_id} · {new Date(r.created_at).toLocaleString("bg")}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3 text-xs">
                <div>
                  <div className="opacity-60">Текуща стойност</div>
                  <div className="font-mono">
                    {r.current_value != null ? `${r.current_value} ${r.current_unit || ""}` : "—"}
                  </div>
                </div>
                <div>
                  <div className="opacity-60">Предложена от потребителя</div>
                  <div className="font-mono">
                    {r.reported_value != null ? `${r.reported_value} ${r.reported_unit || ""}` : "—"}
                  </div>
                </div>
              </div>

              <div className={`text-xs p-2.5 rounded-lg mb-3 ${isDark ? "bg-slate-800" : "bg-slate-50"}`}>
                <b>Причина:</b> {r.reason}
              </div>

              {isPending && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
                    <input
                      type="text" inputMode="decimal"
                      placeholder="Правилна стойност"
                      value={d.value || ""}
                      onChange={(e) => setDraft(r._id, { value: e.target.value })}
                      className={`text-xs p-2 rounded-lg border ${
                        isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-300"
                      }`}
                      data-testid={`admin-marker-report-corrected-value-${r._id}`}
                    />
                    <input
                      type="text"
                      placeholder="Мерна единица"
                      value={d.unit || ""}
                      onChange={(e) => setDraft(r._id, { unit: e.target.value })}
                      className={`text-xs p-2 rounded-lg border ${
                        isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-300"
                      }`}
                    />
                    <input
                      type="text"
                      placeholder="Бележка (по избор)"
                      value={d.note || ""}
                      onChange={(e) => setDraft(r._id, { note: e.target.value })}
                      className={`text-xs p-2 rounded-lg border ${
                        isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-300"
                      }`}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => accept(r)}
                      disabled={isBusy}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-60"
                      data-testid={`admin-marker-report-accept-${r._id}`}
                    >
                      {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                      Приеми {d.value ? "и коригирай" : "(без промяна)"}
                    </button>
                    <button
                      onClick={() => reject(r)}
                      disabled={isBusy}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-500 text-white hover:bg-slate-600 disabled:opacity-60"
                      data-testid={`admin-marker-report-reject-${r._id}`}
                    >
                      <XCircle className="w-3 h-3" />
                      Отхвърли
                    </button>
                  </div>
                </>
              )}

              {!isPending && (r.resolution_note || r.corrected_value != null) && (
                <div className="text-[11px] opacity-70 mt-1">
                  {r.corrected_value != null && (
                    <div>Коригирана стойност: {r.corrected_value} {r.corrected_unit || ""} · Приложено в lab_entries: {r.applied_to_lab_entries ? "да" : "не"}</div>
                  )}
                  {r.resolution_note && <div>Бележка: {r.resolution_note}</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
